import type { SxProps, Theme } from "@mui/material";
import { Autocomplete, createFilterOptions, TextField } from "@mui/material";
import {
  CountryCode,
  getCountries,
  getCountryCallingCode,
} from "libphonenumber-js";

const regionNames = new Intl.DisplayNames(["en"], { type: "region" });

export const phoneCountryOptions = getCountries().map((iso2) => ({
  iso2,
  dialCode: `+${getCountryCallingCode(iso2)}`,
}));

type PhoneCountryOption = (typeof phoneCountryOptions)[number];

export const phoneCountryMap = new Map(
  phoneCountryOptions.map((opt) => [opt.iso2, opt]),
);

const filterOptions = createFilterOptions<PhoneCountryOption>({
  stringify: (opt) => `${opt.dialCode} ${opt.iso2} ${regionNames.of(opt.iso2)}`,
});

interface Props {
  value?: string;
  onChange: (iso2: string, dialCode: string) => void;
  sx?: SxProps<Theme>;
}

function PhoneCountryCodeSelect({ value, onChange, sx }: Props) {
  const selectedOption = phoneCountryMap.get(value as CountryCode) ?? null;

  return (
    <Autocomplete<PhoneCountryOption>
      options={phoneCountryOptions}
      value={selectedOption}
      onChange={(_, option) => {
        if (option) onChange(option.iso2, option.dialCode);
      }}
      getOptionLabel={(opt) => opt.dialCode}
      isOptionEqualToValue={(opt, val) => opt.iso2 === val.iso2}
      filterOptions={filterOptions}
      sx={{ width: 80, ...sx }}
      slotProps={{
        clearIndicator: { sx: { display: "none" } },
        popupIndicator: { sx: { display: "none" } },
        popper: { style: { minWidth: 220 }, placement: "bottom-start" },
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Code"
          variant="outlined"
          sx={{
            "& .MuiInputBase-root": {
              paddingRight: "14px !important",
              paddingLeft: "14px !important",
            },
          }}
        />
      )}
      renderOption={(props, opt) => {
        const { key, ...optionProps } = props as typeof props & { key: string };
        return (
          <li
            key={key}
            {...optionProps}
            style={{ display: "flex", gap: 8, fontSize: 14 }}
          >
            <span style={{ fontWeight: 500 }}>{opt.dialCode}</span>
            <span
              style={{
                color: "inherit",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {regionNames.of(opt.iso2)}
            </span>
          </li>
        );
      }}
    />
  );
}

export default PhoneCountryCodeSelect;
